import { axiosInstance } from "../../Authentication/AxiosInterceptor";
import {
  CurateAccessClientsDto,
  CurateAdminDirectoryDto,
  CurateResolveDto,
  CurateResourceListItemDto,
  CurateResourceDetailDto,
  CurateFolderDto,
  CurateSourcePagesDto,
  CrawlCurateSourceResultDto,
  CurateResourceStatsDto,
} from "../../Types/Curate/CurateResource";

export async function getCurateAdminDirectory(params: {
  providerId?: string;
  q?: string;
  page?: number;
  pageSize?: number;
  folderId?: string;
}): Promise<CurateAdminDirectoryDto> {
  const response = await axiosInstance.get("GetCurateAdminDirectory", { params });
  return response.data as CurateAdminDirectoryDto;
}

export async function getCurateAccessClients(params: {
  providerId: string;
  resourceId: string;
  q?: string;
  page?: number;
  pageSize?: number;
  includeChildren?: boolean;
}): Promise<CurateAccessClientsDto> {
  const { providerId, resourceId, ...query } = params;
  const response = await axiosInstance.get(
    `GetCurateAccessClients/${providerId}/${resourceId}`,
    { params: query }
  );
  return response.data as CurateAccessClientsDto;
}

export async function grantCurateAccess(body: {
  resourceId: string;
  providerId: string;
  clientIds: string[];
  add: boolean;
  includeChildren?: boolean;
}): Promise<{
  resourceId: string;
  clientIds: string[];
  grantCount: number;
  add: boolean;
}> {
  const response = await axiosInstance.post("GrantCurateAccess", body);
  return response.data;
}

export async function getCurateFolderAccessClients(params: {
  providerId: string;
  folderId: string;
  q?: string;
  page?: number;
  pageSize?: number;
}): Promise<CurateAccessClientsDto> {
  const { providerId, folderId, ...query } = params;
  const response = await axiosInstance.get(
    `GetCurateFolderAccessClients/${providerId}/${folderId}`,
    { params: query }
  );
  return response.data as CurateAccessClientsDto;
}

export async function grantCurateFolderAccess(body: {
  providerId: string;
  folderId: string;
  clientIds: string[];
  add: boolean;
}): Promise<{
  folderId: string;
  resourceCount: number;
  clientCount: number;
  updatedCount: number;
  add: boolean;
}> {
  const response = await axiosInstance.post("GrantCurateFolderAccess", body);
  return response.data;
}

export async function resolveCurateResource(
  providerId: string,
  resourceId: string,
  clientId: string,
  page?: number
): Promise<CurateResolveDto> {
  const response = await axiosInstance.get(
    `ResolveCurateResource/${providerId}/${resourceId}`,
    { params: { clientId, ...(page && page >= 1 ? { page } : {}) } }
  );
  return response.data as CurateResolveDto;
}

export async function fetchCurateResourceBytes(
  providerId: string,
  resourceId: string,
  clientId: string,
  download = false
): Promise<ArrayBuffer> {
  const response = await axiosInstance.get<ArrayBuffer>(
    `GetCurateResourceFile/${providerId}/${resourceId}`,
    {
      params: { clientId, ...(download ? { download: "1" } : {}) },
      responseType: "arraybuffer",
    }
  );
  return response.data;
}

export async function uploadCurateResource(form: FormData): Promise<CurateResourceListItemDto> {
  const response = await axiosInstance.post("UploadCurateResource", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return response.data as CurateResourceListItemDto;
}

export async function updateCurateResource(body: {
  providerId: string;
  resourceId: string;
  title?: string;
  description?: string;
  folderId?: string;
  url?: string;
  resourceType?: string;
  approvedBy?: string;
  isActive?: boolean;
  isApprovedSource?: boolean;
  maxPages?: number;
  autoDisableChildPages?: boolean;
}): Promise<CurateResourceListItemDto> {
  const response = await axiosInstance.put("UpdateCurateResource", body);
  return response.data as CurateResourceListItemDto;
}

export async function createCurateSource(body: {
  providerId?: string;
  folderId: string;
  name: string;
  url: string;
  resourceType?: string;
  approvedBy?: string;
  description?: string;
  isActive?: boolean;
  isApprovedSource?: boolean;
  maxPages?: number;
  autoDisableChildPages?: boolean;
}): Promise<CurateResourceListItemDto> {
  const response = await axiosInstance.post("CreateCurateSource", body);
  return response.data as CurateResourceListItemDto;
}

export async function getCurateResource(
  providerId: string,
  resourceId: string
): Promise<CurateResourceDetailDto> {
  const response = await axiosInstance.get(`GetCurateResource/${providerId}/${resourceId}`);
  return response.data as CurateResourceDetailDto;
}

export async function getCurateSourcePages(params: {
  providerId: string;
  sourceId: string;
  q?: string;
  page?: number;
  pageSize?: number;
}): Promise<CurateSourcePagesDto> {
  const { providerId, sourceId, ...query } = params;
  const response = await axiosInstance.get(
    `GetCurateSourcePages/${providerId}/${sourceId}`,
    { params: query }
  );
  return response.data as CurateSourcePagesDto;
}

export async function addCurateSourcePage(body: {
  providerId?: string;
  sourceId: string;
  url: string;
  title?: string;
}): Promise<CurateResourceListItemDto> {
  const response = await axiosInstance.post("AddCurateSourcePage", body);
  return response.data as CurateResourceListItemDto;
}

export async function crawlCurateSource(body: {
  providerId?: string;
  sourceId: string;
  restart?: boolean;
}): Promise<CrawlCurateSourceResultDto> {
  const response = await axiosInstance.post("CrawlCurateSource", body);
  return response.data as CrawlCurateSourceResultDto;
}

export async function getCurateResourceStats(
  providerId: string,
  resourceId: string
): Promise<CurateResourceStatsDto> {
  const response = await axiosInstance.get(
    `GetCurateResourceStats/${providerId}/${resourceId}`
  );
  return response.data as CurateResourceStatsDto;
}

export async function deleteCurateResource(
  providerId: string,
  resourceId: string
): Promise<void> {
  await axiosInstance.delete(`DeleteCurateResource/${providerId}/${resourceId}`);
}

export async function createCurateFolder(body: {
  providerId?: string;
  name: string;
}): Promise<CurateFolderDto> {
  const response = await axiosInstance.post("CreateCurateFolder", body);
  return response.data as CurateFolderDto;
}

export async function renameCurateFolder(body: {
  providerId?: string;
  folderId: string;
  name: string;
}): Promise<CurateFolderDto> {
  const response = await axiosInstance.put("RenameCurateFolder", body);
  return response.data as CurateFolderDto;
}

export async function deleteCurateFolder(
  providerId: string,
  folderId: string
): Promise<void> {
  await axiosInstance.delete(`DeleteCurateFolder/${providerId}/${folderId}`);
}

export async function clearCurateFolderContents(
  providerId: string,
  folderId: string
): Promise<{ deleted: boolean; deletedCount: number }> {
  const response = await axiosInstance.delete(
    `ClearCurateFolderContents/${providerId}/${folderId}`
  );
  return response.data as { deleted: boolean; deletedCount: number };
}

export async function refreshCurateResourceSummary(body: {
  providerId: string;
  resourceId: string;
}): Promise<CurateResourceListItemDto> {
  const response = await axiosInstance.post("RefreshCurateResourceSummary", body);
  return response.data as CurateResourceListItemDto;
}

export async function backfillCurateResourceSummaries(body: {
  providerId?: string;
  folderId?: string;
  onlyEmpty?: boolean;
  limit?: number;
}): Promise<{ queued: number; skipped: number }> {
  const response = await axiosInstance.post("BackfillCurateResourceSummaries", body);
  return response.data as { queued: number; skipped: number };
}

export async function backfillCurateSourceSummaries(body: {
  providerId?: string;
  sourceId: string;
}): Promise<{ queued: number; skipped: number }> {
  const response = await axiosInstance.post("BackfillCurateSourceSummaries", body);
  return response.data as { queued: number; skipped: number };
}
